import { useEffect, useState } from "react";
import { PageContainer } from "../../../components";
import { previewClassApi, professoresApi } from "../../../services";
import Alert from "../../../helpers/Alert";
import { ICategoria } from "../../../types";
import TabsCategoria from "../../../components/tabsCategorias";
import categoriasApi from "../../../services/categoriaApi";
import Title from "../../../components/text/Title";
import { Dt } from "../../../helpers";
import { tw } from "@matheuscaetano/helprs";
import dayjs from "dayjs";
import { PreviewClass } from "../../../models";

type ResumoData = {
  todos_alunos: string[];
  previews: PreviewClass[];
  quantidade_avulsos: number;
  quantidade_ocupados: number;
  categoria_id: number;
  categoria_nome: string;
  categoria_cor: string;
  curso_id: number;
  curso_descricao: string;
  sala_id: number;
  sala_nome: string;
  sala_codigo: string;
  professor_id: number;
  professor_nome: string;
  dia: number;
  idade_aulas: number;
  quantidade_alunos: number;
  duracao: string;
  inicio_aula: string;
  final_aula: string;
};

type FormattedResumoData = {
  professor_id: number;
  professor_nome: string;
  curso_id: number;
  curso_descricao: string;
  sala_id: number;
  sala_nome: string;
  sala_codigo: string;
  idade_aulas: number;
  cores: {
    text: string;
    background: string;
    backgroundInner: string;
    border: string;
  };
  horarios: {
    dia: number;
    inicio_aula: string;
    final_aula: string;
    quantidade_alunos: number;
    duracao: string;
    quantidade_avulsos: number;
    quantidade_ocupados: number;
    previews: PreviewClass[];
    todos_alunos: string[];
  }[];
  salas: {
    sala_id: number;
    sala_nome: string;
    sala_codigo: string;
  }[];
};

const currentWeekSunday = dayjs().startOf("week");
const daysOfWeek = {
  0: currentWeekSunday.format("YYYY-MM-DD"), // Sunday
  1: currentWeekSunday.add(1, "day").format("YYYY-MM-DD"), // Monday
  2: currentWeekSunday.add(2, "day").format("YYYY-MM-DD"), // Tuesday
  3: currentWeekSunday.add(3, "day").format("YYYY-MM-DD"), // Wednesday
  4: currentWeekSunday.add(4, "day").format("YYYY-MM-DD"), // Thursday
  5: currentWeekSunday.add(5, "day").format("YYYY-MM-DD"), // Friday
  6: currentWeekSunday.add(6, "day").format("YYYY-MM-DD"), // Saturday
};

export default function ResumoProfessores() {
  const queryParams = new URLSearchParams(window.location.search);
  const categoriaIdFromQuery = queryParams.get("categoria_id");

  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState<ICategoria[]>([]);
  const [data, setData] = useState<ResumoData[]>([]);
  const [formattedData, setFormattedData] = useState<FormattedResumoData[]>([]);
  const [categoriaId, setCategoriaId] = useState<number>(
    categoriaIdFromQuery ? Number(categoriaIdFromQuery) : 1
  );

  async function loadCategories() {
    const { data, isError } = await categoriasApi.listAll();
    if (isError) return Alert.error("Erro ao carregar categorias");
    setCategorias(data);
  }

  async function loadResumo() {
    setLoading(true);
    const { data, isError } = await professoresApi.getResumo({
      categoria_id: categoriaId,
    });
    setLoading(false);
    if (isError) return Alert.error("Erro ao carregar resumo");
    setData(data);
  }

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadResumo();
  }, [categoriaId]);

  useEffect(() => {
    const groupedData = data.reduce((acc: FormattedResumoData[], item) => {
      const existingProfessor = acc.find(
        (professor) =>
          professor.professor_id === item.professor_id &&
          professor.curso_id === item.curso_id
      );

      //? Cria um novo objeto de sala
      let horarios = data
        .filter(
          (row) =>
            row.sala_id === item.sala_id &&
            row.professor_id === item.professor_id &&
            row.curso_id === item.curso_id
        )
        .map((row) => ({
          dia: row.dia,
          inicio_aula: row.inicio_aula,
          final_aula: row.final_aula,
          quantidade_alunos: row.quantidade_alunos,
          duracao: row.duracao,
          quantidade_avulsos: row.quantidade_avulsos,
          quantidade_ocupados: row.quantidade_ocupados,
          todos_alunos: row.todos_alunos,
          previews: row.previews,
        }));

      const sala = {
        sala_id: item.sala_id,
        sala_nome: item.sala_nome,
        sala_codigo: item.sala_codigo,
      };

      //? Adicionar a sala ao item
      if (existingProfessor) {
        existingProfessor.salas.push(sala);
      } else {
        acc.push({
          cores: {
            border: `border-${item.categoria_cor}`,
            background: `bg-${item.categoria_cor}-03`,
            backgroundInner: `bg-${item.categoria_cor}`,
            text: `text-${item.categoria_cor}`,
          },
          professor_id: item.professor_id,
          professor_nome: item.professor_nome.toUpperCase(),
          curso_id: item.curso_id,
          curso_descricao: item.curso_descricao.toUpperCase(),
          idade_aulas: item.idade_aulas,
          sala_id: item.sala_id,
          sala_nome: item.sala_nome,
          sala_codigo: item.sala_codigo,
          horarios,
          salas: [sala],
        });
      }

      //? Remove duplicates in salas
      if (existingProfessor) {
        existingProfessor.salas = existingProfessor.salas.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.sala_id === value.sala_id)
        );
      }

      return acc;
    }, []);

    setFormattedData(groupedData);
  }, [data]);

  async function deletePreview(previewId?: number) {
    if (!previewId) return;
    const confirm = await Alert.confirm(
      "Tem certeza que deseja excluir?",
      "Excluir?"
    );
    if (!confirm) return;
    const { isError } = await previewClassApi.destroy(previewId);
    if (isError) {
      Alert.error("Erro ao excluir preview");
      return;
    }
    Alert.success("Preview excluído com sucesso");
    loadResumo();
  }

  async function onInputLoseFocus(
    e: React.FocusEvent<HTMLInputElement>,
    group: FormattedResumoData,
    horario: FormattedResumoData["horarios"][number],
    preview?: PreviewClass
  ) {
    const inputValue = e.target.value.trim();
    if (!inputValue || inputValue === preview?.nome) return;
    console.log("Input value lost focus:", inputValue);

    if (preview?.id) {
      //? Update existing preview
      const { isError } = await previewClassApi.update(preview.id, {
        nome: inputValue,
      });

      if (isError) {
        Alert.error("Erro ao excluir preview");
      }
    } else {
      const { isError } = await previewClassApi.create({
        nome: inputValue,
        professor_id: group.professor_id,
        curso_id: group.curso_id,
        inicio_aula: horario.inicio_aula,
        final_aula: horario.final_aula,
        data: (daysOfWeek as any)[horario.dia],
      });

      if (isError) {
        Alert.error("Erro ao excluir preview");
      }
    }

    loadResumo();
    e.target.value = "";
  }

  return (
    <PageContainer>
      <header className="max-w-[800px]">
        <TabsCategoria
          categorias={categorias}
          categoriaId={categoriaId}
          setCategoriaId={setCategoriaId}
        />
      </header>

      <main>
        {loading && !formattedData?.length ? (
          <Title>Carregando...</Title>
        ) : !formattedData?.length ? (
          <h1>Nenhum dado</h1>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {formattedData.map((group, i) => (
              <div
                key={i}
                className={tw(
                  `mb-5 border border-2 rounded-2xl p-3`,
                  group.cores.border,
                  group.cores.background
                )}
              >
                <header className="flex justify-between items-center">
                  <h1 className="text-lg font-bold flex-1 truncate">
                    {group.professor_nome}
                  </h1>

                  <span
                    className={tw(
                      "border border-2 rounded-lg p-1 font-bold",
                      group.cores.border,
                      group.cores.text
                    )}
                  >
                    {group.idade_aulas}+
                  </span>
                </header>

                <div className="flex gap-2 items-center">
                  <div
                    className={tw(
                      "border border-gray-700 rounded px-1",
                      group.cores.backgroundInner
                    )}
                  >
                    {group.curso_descricao}
                  </div>

                  <div className="flex gap-2 items-center">
                    {group.salas.map((sala) => (
                      <div
                        className={tw(
                          "border border-gray-700 rounded px-1",
                          group.cores.backgroundInner
                        )}
                        key={sala.sala_codigo}
                      >
                        {sala.sala_codigo}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div>
                    {group.horarios.map((horario, j) => (
                      <div className="grid grid-cols-12 gap-1 mt-4" key={j}>
                        <div
                          key={j}
                          className="col-span-2 border rounded p-0.5 px-1 font-bold flex items-center justify-center truncate min-h-[30px] border-gray-700 bg-white"
                        >
                          {Dt.WEEK_DAYS_MAP[horario.dia].shortName}
                        </div>

                        <div className="col-span-10">
                          <header className="">
                            <div className="flex justify-between items-center gap-1">
                              <span className="border rounded border-gray-700 px-1 text-center bg-white">
                                {horario.quantidade_ocupados +
                                  horario.previews.length}
                                /{horario.quantidade_alunos}
                              </span>
                              <span className="border rounded border-gray-700 px-1 flex-1 text-center bg-white">
                                {Dt.formatStartEndTime(
                                  horario.inicio_aula,
                                  horario.final_aula
                                )}
                                H
                              </span>
                            </div>
                          </header>

                          <main className="flex flex-col gap-1 pt-1">
                            {Array.from(
                              { length: horario.quantidade_alunos },
                              () => 0
                            ).map((_, index) =>
                              horario.todos_alunos[index] ? (
                                <div
                                  key={"A" + index}
                                  className="border rounded flex-1 text-center bg-gray-200 border-gray-400"
                                >
                                  <span className="text-sm px-1">
                                    {horario.todos_alunos[index]
                                      .split("(")[0]
                                      .trim()}
                                    <span className="text-xs ml-1">
                                      (
                                      {horario.todos_alunos[index]
                                        .split("(")[1]
                                        .trim()}
                                    </span>
                                  </span>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <div
                                    key={"B" + index}
                                    className="border rounded flex-1 text-center bg-white border-gray-900"
                                  >
                                    <input
                                      key={
                                        horario.previews[
                                          index - horario.todos_alunos?.length
                                        ]?.nome +
                                        index +
                                        "i"
                                      }
                                      type="text"
                                      className="w-full h-full text-center text-sm px-1 bg-transparent uppercase"
                                      defaultValue={
                                        horario.previews[
                                          index - horario.todos_alunos?.length
                                        ]?.nome || ""
                                      }
                                      onBlur={(e) =>
                                        onInputLoseFocus(
                                          e,
                                          group,
                                          horario,
                                          horario.previews[
                                            index - horario.todos_alunos?.length
                                          ]
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          (e.target as any).blur();
                                        }
                                      }}
                                    />
                                  </div>
                                  <div
                                    key={
                                      horario.previews[
                                        index - horario.todos_alunos?.length
                                      ]?.nome +
                                      index +
                                      "b"
                                    }
                                    onClick={() =>
                                      deletePreview(
                                        horario.previews[
                                          index - horario.todos_alunos?.length
                                        ]?.id
                                      )
                                    }
                                    className={tw(
                                      "border rounded border-gray-700 text-center px-1 pt-1 place-items-center   bg-white",
                                      !horario.previews[
                                        index - horario.todos_alunos?.length
                                      ]?.id
                                        ? "opacity-50"
                                        : "hover:bg-red-300 cursor-pointer"
                                    )}
                                  >
                                    <img
                                      src="https://cdn-icons-png.flaticon.com/512/4021/4021663.png"
                                      alt="excluir aluno"
                                      className="w-[15px] h-[15px]"
                                    />
                                  </div>
                                </div>
                              )
                            )}
                          </main>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* <pre className="grid grid-cols-1 md:grid-cols-3">
        <code>{JSON.stringify(data, null, 4)}</code>
        <code>{JSON.stringify(formattedData, null, 4)}</code>
      </pre> */}
    </PageContainer>
  );
}
